import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Send, Store } from 'lucide-react';
import { Button, Card, Skeleton, Textarea, cn } from '@mlm/ui';
import {
  listConversations,
  listChatMessages,
  markConversationRead,
  sendChatMessage,
  type ConversationListItem,
} from '../lib/chat-api';

/** House conversations show the store name; merchant threads show the business. */
function counterpartyLabel(c: Pick<ConversationListItem, 'sellerKind' | 'counterparty'>): string {
  if (c.sellerKind === 'house') return 'Store Support';
  return c.counterparty ?? 'Merchant';
}

function relativeTime(iso: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString();
}

/** /messages — the buyer's conversation inbox. Slow poll (30s). */
export function MessagesInboxPage() {
  const q = useQuery({
    queryKey: ['chat', 'conversations'],
    queryFn: () => listConversations({ limit: 30 }),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const items = q.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-o" />
        <h1 className="text-xl font-semibold">Messages</h1>
      </div>

      {q.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
          <MessageCircle className="h-8 w-8" />
          <p className="text-sm">No conversations yet.</p>
          <p className="text-xs">Message a seller from any product page to start a chat.</p>
        </Card>
      ) : (
        <Card className="divide-y overflow-hidden">
          {items.map((c) => (
            <Link
              key={c.id}
              to={`/messages/${c.id}`}
              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/50"
            >
              <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-o-l text-o">
                <Store className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={cn('truncate text-sm', c.unread && 'font-semibold')}>
                    {counterpartyLabel(c)}
                  </span>
                  <span className="flex-none text-[11px] text-muted-foreground">
                    {relativeTime(c.lastMessageAt)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <p
                    className={cn(
                      'min-w-0 flex-1 truncate text-xs',
                      c.unread ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {c.lastMessageSenderKind === 'buyer' && 'You: '}
                    {c.lastMessagePreview ?? 'No messages yet'}
                  </p>
                  {c.unread && <span className="h-2 w-2 flex-none rounded-full bg-o" aria-hidden />}
                </div>
              </div>
            </Link>
          ))}
        </Card>
      )}
    </div>
  );
}

/** /messages/:id — a single thread. Fast poll (4s) while mounted. */
export function MessageThreadPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Header label: reuse the cached inbox row if present, else a neutral fallback.
  const convosCache = qc.getQueryData<{ items: ConversationListItem[] }>(['chat', 'conversations']);
  const headerConvo = convosCache?.items.find((c) => c.id === id);

  const msgsQ = useInfiniteQuery({
    queryKey: ['chat', 'thread', id, 'messages'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => listChatMessages(id, { limit: 30, cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    refetchInterval: 4_000,
    refetchOnWindowFocus: true,
  });

  // Newest-first pages flattened, then reversed for chronological display.
  const messages = useMemo(
    () => (msgsQ.data?.pages.flatMap((p) => p.items) ?? []).slice().reverse(),
    [msgsQ.data],
  );

  const markRead = useMutation({
    mutationFn: () => markConversationRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat', 'unread-count'] });
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    },
  });

  // Mark read on open and whenever new incoming messages arrive while viewing.
  const lastIncomingId = messages.filter((m) => m.senderKind === 'seller').at(-1)?.id;
  useEffect(() => {
    if (id) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, lastIncomingId]);

  const send = useMutation({
    mutationFn: (body: string) => sendChatMessage(id, body),
    onSuccess: () => {
      setDraft('');
      qc.invalidateQueries({ queryKey: ['chat', 'thread', id, 'messages'] });
      qc.invalidateQueries({ queryKey: ['chat', 'conversations'] });
    },
  });
  // An admin can block a member from messaging — the send 403s with this code.
  const blocked =
    (send.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error ===
    'user_chat_blocked';

  // Keep the latest message in view.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  function submit() {
    const body = draft.trim();
    if (!body || send.isPending) return;
    send.mutate(body);
  }

  return (
    <div className="flex h-[calc(100vh-12rem)] flex-col">
      <div className="flex items-center gap-2 border-b pb-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/messages')}
          className="md:hidden"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-o-l text-o">
          <Store className="h-4 w-4" />
        </div>
        <div className="font-medium">
          {headerConvo ? counterpartyLabel(headerConvo) : 'Conversation'}
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto py-4">
        {msgsQ.hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              disabled={msgsQ.isFetchingNextPage}
              onClick={() => msgsQ.fetchNextPage()}
            >
              Load earlier messages
            </Button>
          </div>
        )}
        {msgsQ.isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="ml-auto h-10 w-40" />
          </div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={cn('flex', m.senderKind === 'buyer' ? 'justify-end' : 'justify-start')}
            >
              <div
                className={cn(
                  'max-w-[75%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm',
                  m.senderKind === 'buyer'
                    ? 'rounded-br-sm bg-o text-white'
                    : 'rounded-bl-sm bg-muted text-foreground',
                )}
              >
                {m.body}
                <span
                  className={cn(
                    'mt-1 block text-right text-[10px]',
                    m.senderKind === 'buyer' ? 'text-white/70' : 'text-muted-foreground',
                  )}
                >
                  {relativeTime(m.createdAt)}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {blocked && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          You've been blocked from messaging. Please contact support if you think this is a mistake.
        </div>
      )}
      <div className="flex items-end gap-2 border-t pt-3">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={blocked ? 'Messaging unavailable' : 'Type a message…'}
          rows={1}
          maxLength={4000}
          disabled={blocked}
          className="max-h-32 min-h-[2.5rem] flex-1 resize-none"
        />
        <Button
          onClick={submit}
          disabled={!draft.trim() || send.isPending || blocked}
          className="flex-none"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
